import type React from "react";
import { useState, useEffect } from "react";
import { Tabs, Tab, Box, CircularProgress, Alert } from "@mui/material";
import { useDispatch } from "react-redux";
import { useNavigate } from "react-router-dom";
import { toast } from "react-toastify";
import { DashboardData } from "./types/dashboard";
import apiService from "../../service/Api";
import { logout } from "../../store/slice/AuthSlice";
import AdminDashboard from "./components/admin-widgets/AdminDashboard";
import SalesDashboard from "./components/sales-widgets/SalesDashboard";
import OperationsDashboard from "./components/operations-widgets/OperationsDashboard";
import CeoDashboard from "./components/ceo-widgets/CeoDashboard";
import VenuesDashboard from "./components/venues-widgets/VenuesDashboard";
import PixelPilotsDashboard from "./components/pixel-pilots/PixelPilotsDashboard";
import ChemaDashboard from "./components/chema-widgets/ChemaDashboard";
import FuneralHomesDashboard from "./components/funeral-homes/FuneralHomesDashboard";
import InsuranceCompaniesDashboard from "./components/insurance-companies/InsuranceCompaniesDashboard";
import AcademyDashboard from "./components/academy-widgets/AcademyDashboard";
import MyDashboardDashboard from "./components/my-dashboard-widgets/MyDashboard";
import MyBalanceDashboard from "./components/my-balance-widgets/MyBalanceDashboard";
import ContactsDashboard from "./components/contacts-widgets/ContactsDashboard";

interface TabPanelProps {
  children?: React.ReactNode;
  index: number;
  value: number;
}

const TabPanel: React.FC<TabPanelProps> = ({ children, value, index }) => {
  return (
    <div
      role="tabpanel"
      hidden={value !== index}
      id={`dashboard-tabpanel-${index}`}
      aria-labelledby={`dashboard-tab-${index}`}
    >
      {value === index && <Box sx={{ py: 3 }}>{children}</Box>}
    </div>
  );
};

const MainDashboard: React.FC = () => {
  const [tabValue, setTabValue] = useState(0);
  const [dashboardData, setDashboardData] = useState<DashboardData | null>(
    null,
  );
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const dispatch = useDispatch();
  const navigate = useNavigate();

  const handleTabChange = (event: React.SyntheticEvent, newValue: number) => {
    setTabValue(newValue);
  };

  useEffect(() => {
    const fetchDashboardData = async () => {
      setIsLoading(true);
      setError(null);
      try {
        const response = await apiService.get<DashboardData>(
          "/api/management/dashboard/statistics/",
        );
        setDashboardData(response.data);
      } catch (err: unknown) {
        const errorResponse = (
          err as {
            response?: { status?: number; data?: { detail?: string } };
          }
        )?.response;
        let errorMessage =
          "Failed to load dashboard statistics. Please try again.";
        if (errorResponse?.status === 401) {
          errorMessage = "Unauthorized: Please log in again.";
          dispatch(logout());
          navigate("/login");
        } else if (errorResponse?.status === 403) {
          errorMessage =
            "You do not have permission to access dashboard statistics.";
        } else if (errorResponse?.data?.detail) {
          errorMessage = errorResponse.data.detail;
        }
        setError(errorMessage);
        toast.error(errorMessage, {
          position: "top-right",
          autoClose: 5000,
          theme: "light",
        });
      } finally {
        setIsLoading(false);
      }
    };

    fetchDashboardData();
  }, [dispatch, navigate]);

  return (
    <Box>
      <Tabs
        value={tabValue}
        onChange={handleTabChange}
        aria-label="dashboard tabs"
        variant="scrollable"
        scrollButtons="auto"
        allowScrollButtonsMobile
      >
        <Tab label="Admin" id="dashboard-tab-0" />
        <Tab label="My Dashboard" id="dashboard-tab-1" />

        <Tab label="Sales" id="dashboard-tab-2" />
        <Tab label="Operations" id="dashboard-tab-3" />
        <Tab label="CEO" id="dashboard-tab-4" />
        <Tab label="Contacts" id="dashboard-tab-5" />
        <Tab label="Venues" id="dashboard-tab-6" />
        <Tab label="Pixel Pilots" id="dashboard-tab-7" />
        <Tab label="Academy" id="dashboard-tab-8" />
        <Tab label="CHEMA" id="dashboard-tab-9" />
        <Tab label="Funeral Homes" id="dashboard-tab-10" />
        <Tab label="Insurance Companies" id="dashboard-tab-11" />
        <Tab label="My Balance" id="dashboard-tab-12" />
      </Tabs>

      {isLoading && (
        <Box display="flex" justifyContent="center" my={4}>
          <CircularProgress />
        </Box>
      )}

      {error && (
        <Alert severity="error" sx={{ mb: 2 }}>
          {error}
        </Alert>
      )}

      <TabPanel value={tabValue} index={0}>
        {dashboardData && <AdminDashboard data={dashboardData} />}
      </TabPanel>

      <TabPanel value={tabValue} index={1}>
        <MyDashboardDashboard />
      </TabPanel>

      <TabPanel value={tabValue} index={2}>
        <SalesDashboard />
      </TabPanel>

      <TabPanel value={tabValue} index={3}>
        <OperationsDashboard />
      </TabPanel>

      <TabPanel value={tabValue} index={4}>
        <CeoDashboard />
      </TabPanel>

      <TabPanel value={tabValue} index={5}>
        <ContactsDashboard />
      </TabPanel>

      <TabPanel value={tabValue} index={6}>
        <VenuesDashboard />
      </TabPanel>

      <TabPanel value={tabValue} index={7}>
        <PixelPilotsDashboard />
      </TabPanel>

      <TabPanel value={tabValue} index={8}>
        <AcademyDashboard />
      </TabPanel>

      <TabPanel value={tabValue} index={9}>
        <ChemaDashboard />
      </TabPanel>

      <TabPanel value={tabValue} index={10}>
        <FuneralHomesDashboard />
      </TabPanel>

      <TabPanel value={tabValue} index={11}>
        <InsuranceCompaniesDashboard />
      </TabPanel>

      <TabPanel value={tabValue} index={12}>
        <MyBalanceDashboard />
      </TabPanel>
    </Box>
  );
};

export default MainDashboard;
